/**
 * Project: academy-v2
 * File: one-to-one
 * Author: wanpeninsula
 * Author URI: https://www.pennycodes.dev
 * Created: 10/01/2024 at 6:15 am
 *
 * Copyright (c) 2024 academy-v2. All rights reserved.
 */
import routes from "@/config/routes.ts";
import client from "@/data/client";
import API_ENDPOINTS from "@/data/client/endpoints.ts";
import {
  DefaultErrorResponse,
  NewTutorAvailabilityItem,
  ScheduleSpeakingClassesAvailable,
  ScheduleSpeakingClassesEvents,
  ScheduleSpeakingClassesPagination,
  SpeakingClassesAvailable,
  TutorAvailabilityItem,
  TutorAvailabilityParams,
  UserOneToOneCredits,
  UserOneToOneTopicsDone,
} from "@/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * @deprecated use useTutorAvailabilities instead
 * @param params
 * @param disable
 */
export function useTutorAvailabilitiesOld(
  params: Partial<TutorAvailabilityParams>,
  disable?: boolean,
) {
  const { data, isLoading, error } = useQuery<TutorAvailabilityItem, Error>(
    [API_ENDPOINTS.AVAILABILITIES_TUTOR, params],
    ({ queryKey }) =>
      client.oneToOne.tutorAvailabilities(Object.assign({}, queryKey[1])),
    {
      enabled: !disable,
    },
  );
  return {
    availabilities: data,
    isLoading,
    error,
  };
}

export function useBookSpeakingClass() {
  const { data, isLoading, error, refetch } = useQuery<
    SpeakingClassesAvailable,
    Error
  >(
    [API_ENDPOINTS.SPEAKING_CLASS_TUTOR_AND_TOPIC_LIST],
    client.speakingClass.speakingTutorAndTopicList,
  );
  return {
    events: data?.item?.events,
    isLoading,
    error,
    refetch,
  };
}

export function useBookSpeakingClassMutation(callback?: () => void) {
  const navigate = useNavigate();

  return useMutation(client.speakingClass.bookSpeakingClass, {
    onSuccess: (data) => {
      if (data.item) {
        enqueueSnackbar("Booking successful", {
          variant: "success",
        });
        if (callback) {
          callback();
        }
        navigate(routes.agenda, {
          replace: true,
        });
      } else {
        enqueueSnackbar("Something went wrong. please retry", {
          variant: "error",
        });
      }
    },
    onError: (error) => {
      const err = error as AxiosError<DefaultErrorResponse>;

      enqueueSnackbar(err.response?.data.message, {
        variant: "error",
      });
    },
  });
}

export function useTutorAvailabilities(
  params: Partial<TutorAvailabilityParams>,
  disable?: boolean,
) {
  const { data, isLoading, error } = useQuery<NewTutorAvailabilityItem, Error>(
    [API_ENDPOINTS.AVAILABILITIES_TUTOR_ALL, params],
    ({ queryKey }) =>
      client.oneToOne.newTutorOneToOneAvailabilities(
        Object.assign({}, queryKey[1]),
      ),
    {
      enabled: !disable,
    },
  );
  return {
    availabilities: data,
    isLoading,
    error,
  };
}

export function useOneToOneTopics() {
  const { data, isLoading, error } = useQuery<UserOneToOneTopicsDone, Error>(
    [API_ENDPOINTS.ONE_TO_ONE_TOPICS_DONE],
    client.oneToOne.getTopics,
  );
  return {
    oneToOneTopicsDone: data,
    isLoading,
    error,
  };
}

export function useOneToOneCredits() {
  const { data, isLoading, error } = useQuery<UserOneToOneCredits, Error>(
    [API_ENDPOINTS.ONE_TO_ONE_CREDITS],
    client.oneToOne.userCredits,
  );
  return {
    credits: data,
    isLoading,
    error,
  };
}

export function useBookOneToOneMutation(callback?: () => void) {
  const navigate = useNavigate();

  return useMutation(client.oneToOne.book, {
    onSuccess: (data) => {
      if (data.item) {
        enqueueSnackbar("Booking successful", {
          variant: "success",
        });
        if (callback) {
          callback();
        }
        navigate(routes.agenda, {
          replace: true,
        });
      } else {
        enqueueSnackbar("Something went wrong. please retry", {
          variant: "error",
        });
      }
    },
    onError: (error) => {
      const err = error as AxiosError<DefaultErrorResponse>;
      enqueueSnackbar(err.response?.data.message, {
        variant: "error",
      });
    },
  });
}

export function useBookScheduleSpeakingClass(currentPage: number) {
  const { data, isLoading, error, refetch } = useQuery<ScheduleSpeakingClassesAvailable, Error>(
    [API_ENDPOINTS.SPEAKING_CLASS_TUTOR_AND_TOPIC_LIST, currentPage],
    () => client.scheduleSpeakingClasses.getScheduleSpeakingClasses(currentPage),
    { keepPreviousData: true }
  );

  return {
    events: data?.item?.original?.events as ScheduleSpeakingClassesEvents,
    pagination: data?.item?.original?.pagination as ScheduleSpeakingClassesPagination,
    remainingBookings:data?.item.original?.remainingBookings as number,
    allowedBookingsPerWeek:data?.item.original?.allowedBookingsPerWeek as number,
    isLoading,
    error,
    currentPage,
    refetch
  };
}

export function useBookScheduleSpeakingClassMutation(callback?: () => void) {
  // const navigate = useNavigate();
  const [isBookingSuccess, setIsBookingSuccess] = useState(false);

  // return useMutation(client.speakingClass.bookSpeakingClass, {
  const mutation = useMutation(client.scheduleSpeakingClasses.createScheduleSpeakingClass, {
    onSuccess: (data) => {
      if (data.item) {
        enqueueSnackbar("Booking of speaking class successful", {
          variant: "success",
        });
        setIsBookingSuccess(true);
        if (callback) {
          callback();
        }
        // navigate(routes.agenda, {
        //   replace: true,
        // });
      } else {
        enqueueSnackbar("Something went wrong. Please retry", {
          variant: "error",
        });
      }
    },
    onError: (error) => {
      const err = error as AxiosError<DefaultErrorResponse>;

      enqueueSnackbar(err.response?.data.message, {
        variant: "error",
      });
    },
  });

  return {
    ...mutation,
    isBookingSuccess,
    setIsBookingSuccess
  };
}
